// ============================================================================
// SHARED HELPERS: Cashflow ↔ Invoice Source-of-Truth Logic
// ============================================================================
// Extracted from InvoicesPage.jsx fee revision logic.
// Used by AnalyticsDashboardPage.jsx CashflowProjectionSection.
//
// KEY CONCEPT:
//   Before an invoice is raised  → Timeline is source of truth (estimate)
//   Once an invoice is raised    → Invoices override everything for that stage
//
// "Raised" = status is 'issued' or 'paid' (NOT 'draft').
// Draft invoices do NOT override timeline forecasts.
// ============================================================================

// ============================================================================
// CONSTANTS
// ============================================================================
const RAISED_STATUSES = new Set(['issued', 'paid', 'partially_paid']);

// ============================================================================
// getBaseDesignFee(project)
// ============================================================================
// Get base design fee only (excludes CC retainer).
// Use this for non-CC stage amount calculations in cashflow.
// CC retainer amounts are handled separately (PASS 3 in CashflowProjectionSection).
// ============================================================================
export function getBaseDesignFee(project) {
  return Number(project?.budget) || 0;
}

// ============================================================================
// getCurrentTotalFee(project)
// ============================================================================
// Get current TOTAL fee for a project (base fee + CC retainer if active).
// Mirrors InvoicesPage.jsx getCurrentTotalFee exactly.
//
// ⚠️  WARNING: This includes CC retainer. Do NOT use for non-CC stage amount
//     calculations — use getBaseDesignFee() instead. This function is intended
//     for fee revision revaluation logic only.
// ============================================================================
export function getCurrentTotalFee(project) {
  const baseFee = getBaseDesignFee(project);
  let ccRetainer = 0;
  if (project?.retainerConfig?.status === 'active') {
    const monthly = Number(project.retainerConfig.monthlyAmount) || 0;
    const months  = Number(project.retainerConfig.totalMonths) || 0;
    if (months > 0) ccRetainer = monthly * months;
  }
  const miscFee = (Array.isArray(project?.miscFeeScopes) ? project.miscFeeScopes : [])
    .reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
  return baseFee + ccRetainer + miscFee;
}

// ============================================================================
// getEffectiveStagePercent(project, stageKey)
// ============================================================================
// Get effective stage percent: invoicePercent > budgetAllocation fallback.
// Mirrors InvoicesPage.jsx effectiveStagePercent logic.
// ============================================================================
export function getEffectiveStagePercent(project, stageKey) {
  if (!project || !stageKey) return 0;
  const invoicePercent = project.stageTimeline?.[stageKey]?.invoicePercent;
  const allocationPercent = Number(project.budgetAllocation?.[stageKey]) || 0;
  return invoicePercent != null ? Number(invoicePercent) : allocationPercent;
}

// ============================================================================
// buildRaisedInvoiceIndex(invoices)
// ============================================================================
// Build an index of raised invoices: { [projectId]: { [stageKey]: Invoice[] } }
// Only includes invoices with RAISED_STATUSES (issued, paid, partially_paid).
// Draft invoices are excluded — they must NOT override timeline forecasts.
//
// Invoices without a stageKey go into a special '__unstaged' bucket so they
// can still appear in cashflow without conflicting with stage-based entries.
// ============================================================================
export function buildRaisedInvoiceIndex(invoices) {
  const index = {}; // { [projectId]: { [stageKey]: Invoice[] } }

  if (!invoices || !Array.isArray(invoices)) return index;

  invoices.forEach(inv => {
    const status = (inv.status || '').toLowerCase();
    if (!RAISED_STATUSES.has(status)) return; // Skip drafts

    const projectId = inv.projectId || inv.project_id;
    const stageKey  = inv.stageKey  || inv.stage_key;
    if (!projectId) return;

    if (!index[projectId]) index[projectId] = {};

    const key = stageKey || '__unstaged';
    if (!index[projectId][key]) index[projectId][key] = [];
    index[projectId][key].push(inv);
  });

  return index;
}

// ============================================================================
// getRaisedInvoicesForProject(invoiceIndex, projectId)
// ============================================================================
// Flatten all raised invoices for a given project from the index.
// Returns a flat array of all raised invoices regardless of stage.
// ============================================================================
export function getRaisedInvoicesForProject(invoiceIndex, projectId) {
  const projectBuckets = invoiceIndex[projectId];
  if (!projectBuckets) return [];
  return Object.values(projectBuckets).flat();
}

// ============================================================================
// computePlannedAtNewFee(inv, newFee, project) — INTERNAL
// ============================================================================
// Compute what a single issued invoice "would have been" at a new fee.
// Mirrors InvoicesPage.jsx computePlannedAtNewFee exactly (7-step rule).
// ============================================================================
function computePlannedAtNewFee(inv, newFee, project) {
  const basisMode = inv.basisMode || 'percent';
  const pctSnap = inv.invoicePercentSnapshot;
  const stageKey = inv.stageKey;

  // Rule 1: percent or percent_plus_adjustment with snapshot
  if ((basisMode === 'percent' || basisMode === 'percent_plus_adjustment') && pctSnap != null) {
    return { value: newFee * pctSnap / 100, estimated: false };
  }
  // Rule 2: lumpsum — does not scale
  if (basisMode === 'lumpsum') {
    return { value: Number(inv.baseAmount) || 0, estimated: false };
  }
  // Rule 3: adjustment — past adjustments are not revalued
  if (basisMode === 'adjustment') {
    return { value: 0, estimated: false };
  }
  // Rule 4: fallback — snapshot missing, use current stage percent
  if (stageKey && project) {
    const pct = getEffectiveStagePercent(project, stageKey);
    return { value: newFee * pct / 100, estimated: true };
  }
  // No stage key and no snapshot — cannot revalue
  return { value: Number(inv.baseAmount) || 0, estimated: true };
}

// ============================================================================
// computeFeeRevisionAdjustmentForCashflow(project, invoiceIndex)
// ============================================================================
// Compute fee revision adjustment for cashflow projection.
// For a given project, computes:
//   - Total already issued (base amounts, frozen historical facts)
//   - Total revalued at current fee (what those invoices "should have been")
//   - adjustmentDue = revalued - issued
//   - hasAdjustmentInvoice = whether a basisMode==='adjustment' invoice exists
//
// This matches InvoicesPage.jsx computeRecalcBreakdown logic.
//
// Returns: { adjustmentDue: number, hasAdjustmentInvoice: boolean,
//            issuedTotalBase: number, issuedTotalAtNewFee: number }
// ============================================================================
export function computeFeeRevisionAdjustmentForCashflow(project, invoiceIndex) {
  const projectBuckets = invoiceIndex[project?.id];
  if (!projectBuckets) {
    return { adjustmentDue: 0, hasAdjustmentInvoice: false, issuedTotalBase: 0, issuedTotalAtNewFee: 0 };
  }

  const allRaised = Object.values(projectBuckets).flat();
  if (allRaised.length === 0) {
    return { adjustmentDue: 0, hasAdjustmentInvoice: false, issuedTotalBase: 0, issuedTotalAtNewFee: 0 };
  }

  const newFee = getCurrentTotalFee(project);

  let issuedTotalBase = 0;
  let issuedTotalAtNewFee = 0;
  let hasAdjustmentInvoice = false;

  allRaised.forEach(inv => {
    if (inv.basisMode === 'adjustment') {
      hasAdjustmentInvoice = true;
    }
    // CC invoices (bare 'cc' or monthly 'cc__YYYY-MM') and misc scope invoices
    // are not subject to fee revision revaluation — they use fixed pricing
    // independent of the base design fee. Including them produces spurious ADJ.
    const sk = inv.stageKey || '';
    if (sk === 'cc' || sk.startsWith('cc__') || sk.startsWith('misc__')) return;
    const issuedBase = Number(inv.baseAmount) || 0;
    const { value: plannedAtNewFee } = computePlannedAtNewFee(inv, newFee, project);
    issuedTotalBase += issuedBase;
    issuedTotalAtNewFee += plannedAtNewFee;
  });

  return {
    adjustmentDue: issuedTotalAtNewFee - issuedTotalBase,
    hasAdjustmentInvoice,
    issuedTotalBase,
    issuedTotalAtNewFee,
  };
}
